import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar, Plus, Truck } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import WeekAtAGlance from '../components/WeekAtAGlance';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';

const STATUS_BADGES = {
  inquiry: { label: 'Inquiry', color: 'border-red-500/30 bg-red-500/10 text-red-400' },
  tentative: { label: 'Tentative', color: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400' },
  confirmed: { label: 'Confirmed', color: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' },
  completed: { label: 'Completed', color: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400' },
};

const formatDate = (d) => {
  if (!d) return '-';
  try { return format(parseISO(d), 'M/d/yy'); } catch { return d; }
};

const Dashboard = () => {
  usePageTitle('Dashboard');
  const { isViewer, user } = useAuth();
  const [myEvents, setMyEvents] = useState([]);
  const [myRuns, setMyRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [eventsRes, runsRes] = await Promise.all([
          dashboardAPI.myEvents(),
          dashboardAPI.myTruckingRuns(),
        ]);
        setMyEvents(eventsRes.data);
        setMyRuns(runsRes.data);
      } catch (error) {
        console.error('Failed to load dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome to AV Factory EMP</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isViewer && (
            <>
              <Button asChild variant="outline" data-testid="quick-add-org">
                <Link to="/organizations/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Organization
                </Link>
              </Button>
              <Button asChild variant="outline" data-testid="quick-add-contact">
                <Link to="/contacts/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Contact
                </Link>
              </Button>
              <Button asChild data-testid="quick-add-event">
                <Link to="/events/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Event
                </Link>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Week at a Glance */}
      <WeekAtAGlance />

      {/* My Upcoming Events */}
      {myEvents.length > 0 && (
        <Card className="border-border/50 bg-card/50" data-testid="my-events-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="h-5 w-5 text-emerald-400" />
              My Upcoming Events
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead className="data-table">Dates</TableHead>
                  <TableHead className="data-table">Event</TableHead>
                  <TableHead className="data-table">Client</TableHead>
                  <TableHead className="data-table">Location</TableHead>
                  <TableHead className="data-table">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {myEvents.map((evt) => {
                  const badge = STATUS_BADGES[evt.status] || STATUS_BADGES.inquiry;
                  return (
                    <TableRow key={evt.id} className="border-border/50 hover:bg-muted/30">
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {formatDate(evt.start_date)} - {formatDate(evt.end_date)}
                      </TableCell>
                      <TableCell>
                        <Link to={`/events/${evt.id}`} className="font-medium text-primary hover:underline" data-testid={`my-event-link-${evt.id}`}>
                          {evt.event_name}
                        </Link>
                        {evt.flex_number && (
                          <span className="ml-2 text-xs text-muted-foreground">#{evt.flex_number}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{evt.client_name || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{evt.location_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={badge.color}>{badge.label}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* My Trucking Runs */}
      {myRuns.length > 0 && (
        <Card className="border-border/50 bg-card/50" data-testid="my-trucking-runs-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Truck className="h-5 w-5 text-orange-400" />
              My Trucking Runs
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead className="data-table">Date</TableHead>
                  <TableHead className="data-table">Time</TableHead>
                  <TableHead className="data-table">Event</TableHead>
                  <TableHead className="data-table">Action</TableHead>
                  <TableHead className="data-table">Location</TableHead>
                  <TableHead className="data-table">Truck</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {myRuns.map((run, idx) => (
                  <TableRow key={`${run.event_id}-${idx}`} className="border-border/50 hover:bg-muted/30">
                    <TableCell className="whitespace-nowrap">{formatDate(run.date)}</TableCell>
                    <TableCell className="text-muted-foreground">{run.time || '-'}</TableCell>
                    <TableCell>
                      <Link to={`/events/${run.event_id}`} className="font-medium text-primary hover:underline" data-testid={`my-run-link-${run.event_id}`}>
                        {run.event_name}
                      </Link>
                      {run.flex_number && (
                        <span className="ml-2 text-xs text-muted-foreground">#{run.flex_number}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{run.action || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">{run.location || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">{run.truck_number || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
